import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { jobsAPI } from '../services/api';
import ApplicationProgress from '../components/shared/ApplicationProgress';
import useApplicationUpdates from '../hooks/useApplicationUpdates';
import { getApplicationProgress } from '../utils/applicationStatusModel';
import './ApplicationView.css';

const ApplicationView = () => {
  const { token } = useParams();
  const [jobs, setJobs] = useState([]);
  const loadApplication = useCallback(options => jobsAPI.getApplicationByToken(token, options), [token]);
  const { data: application, loading, error } = useApplicationUpdates(loadApplication, { identity: token });

  useEffect(() => {
    const loadJobs = async () => {
      try {
        const response = await jobsAPI.getAll(true);
        if (response.success) {
          setJobs(response.data);
        }
      } catch (error) {
        console.error('Error loading jobs:', error);
      }
    };
    loadJobs();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#ffa500';
      case 'reviewing':
        return '#4169e1';
      case 'interviewing':
        return '#9932cc';
      case 'accepted':
        return '#32cd32';
      case 'rejected':
        return '#dc143c';
      default:
        return '#666';
    }
  };

  const getStatusText = (status) => {
    return getApplicationProgress(status).label;
  };

  if (loading) {
    return (
      <div className="application-view-page">
        <div className="application-view-container">
          <p>Loading application...</p>
        </div>
      </div>

    );
  }
  if (error && !application) {
    return (
      <div className="application-view-page">
        <div className="application-view-container">
          <div className="error-message">
            <h2>Application Unavailable</h2>
            <p>{error}</p>
            <p>The application link may be invalid or the application may have been removed.</p>

          </div>
        </div>

      </div>
    );
  }
  if (!application) {
    return null;
  }
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const resumeUrl = application.resume_url
    ? (application.resume_url.startsWith('http')
        ? application.resume_url
        : `${API_BASE_URL}${application.resume_url}`)
    : null;
  return (
    <div className="application-view-page">
      <div className="application-view-container">
        <h1>Application Status</h1>
        <div className="application-card">
          <div className="application-header">
            <div>
              <h2>{application.position_title}</h2>
              <p className="application-date">
                Applied on {new Date(application.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <div
              className="status-badge"
              style={{ backgroundColor: getStatusColor(application.status) }}
            >
              {getStatusText(application.status)}
            </div>
          </div>
          <div className="application-details">
            {error && <p className="application-refresh-error" role="status">{error} Showing the last successfully received status.</p>}
            <ApplicationProgress status={application.status} />
            <div className="detail-section">
              <h3>Personal Information</h3>
              <div className="detail-grid personal-detail-grid">
                <div className="detail-item">
                  <label>Name</label>
                  <p>{application.first_name} {application.last_name}</p>
                </div>
                <div className="detail-item">
                  <label>((Discord))</label>
                  <p>{application.email}</p>
                </div>
                {application.phone && (
                  <div className="detail-item">
                    <label>Phone</label>
                    <p>{application.phone}</p>
                  </div>
                )}
                <div className="detail-item">
                  <label>((UCP Account))</label>
                  <p>{application.ucp_account || 'Not available'}</p>
                </div>
              </div>
              {(application.date_of_birth || application.city) && (
                <div className="detail-grid personal-detail-grid personal-detail-row">
                  {application.date_of_birth && (
                    <div className="detail-item">
                      <label>Date of Birth</label>
                      <p>{new Date(application.date_of_birth).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</p>
                    </div>
                  )}
                  {application.city && (
                    <div className="detail-item">
                      <label>City</label>
                      <p>{application.city}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="detail-grid personal-detail-grid personal-detail-row">
                {application.residential_address && (
                  <div className="detail-item">
                    <label>Residential Address</label>
                    <p>{application.residential_address}</p>
                  </div>
                )}
                {application.routing_number && (
                  <div className="detail-item">
                    <label>Routing Number</label>
                    <p>{application.routing_number}</p>
                  </div>
                )}
              </div>
            </div>

            {application.cover_letter && (
              <div className="detail-section">
                <h3>Cover Letter</h3>
                <div className="cover-letter-content">
                  {application.cover_letter.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>
            )}
            {application.additional_roles && (() => {
              try {
                const roles = typeof application.additional_roles === 'string'
                  ? JSON.parse(application.additional_roles)
                  : application.additional_roles;
                if (Array.isArray(roles) && roles.length > 0) {
                  return (
                    <div className="detail-section">
                      <h3>Other Roles of Interest</h3>
                      <div className="additional-roles-list">
                        {roles.map((roleId, index) => {
                          const role = jobs.find(j => j.id === parseInt(roleId));
                          return (
                            <span key={index} className="role-tag">
                              {role ? role.title : `Role ID: ${roleId}`}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              } catch (e) {
                console.error('Error parsing additional roles:', e);
              }
              return null;
            })()}
            {(resumeUrl || application.portfolio || application.server_record_link || application.character_stats_link) && (
              <div className="detail-section">
                <h3>Additional Links</h3>
                <div className="detail-grid">
                  {resumeUrl && (
                    <div className="detail-item">
                      <label>Resume</label>
                      <p>
                        <a
                          href={resumeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          View Resume
                        </a>
                      </p>
                    </div>
                  )}
                  {application.portfolio && (
                    <div className="detail-item">
                      <label>Portfolio</label>
                      <p>{application.portfolio}</p>
                    </div>
                  )}
                  {application.server_record_link && (
                    <div className="detail-item">
                      <label>Server Record</label>
                      <p>
                        <a
                          href={application.server_record_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          View Server Record
                        </a>

                      </p>

                    </div>

                  )}
                  {application.character_stats_link && (
                    <div className="detail-item">
                      <label>Character Stats</label>

                      <p>
                        <a
                          href={application.character_stats_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          View Character Stats
                        </a>

                      </p>

                    </div>

                  )}
                </div>

              </div>

            )}

            <div className="status-info">
              <h3>Current Status</h3>

              <p>
                Your application is currently <strong>{getStatusText(application.status).toLowerCase()}</strong>.

                {application.status === 'pending' && ' We will review your application and get back to you soon.'}
                {application.status === 'reviewing' && ' Our team is carefully reviewing your application.'}
                {application.status === 'interviewing' && ' We would like to schedule an interview with you. You will be contacted shortly.'}
                {application.status === 'accepted' && ' Congratulations! We would like to move forward with your application.'}
                {application.status === 'rejected' && ' Thank you for your interest. Unfortunately, we have decided to move forward with other candidates.'}
                {application.status === 'archived' && ' Its previous review stage or decision is not available.'}
              </p>

              <p className="last-updated">
                Last updated: {new Date(application.updated_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>

            </div>

          </div>

        </div>


        <div className="back-link">
          <a href="/careers">← Back to Careers</a>

        </div>

      </div>

    </div>

  );
};

export default ApplicationView;

